import React, { useState, useEffect, useCallback } from "react";
import {
  TextField,
  Button,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  Paper,
  Typography,
  Container,
  Modal,
  CircularProgress,
  TableContainer,
  InputAdornment,
  Tooltip,
  IconButton,
  Fade,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { styled } from "@mui/material/styles";
import SearchIcon from "@mui/icons-material/Search";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useParams } from "react-router-dom";
import base64 from "base64-js";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CloseIcon from "@mui/icons-material/Close";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SchoolIcon from "@mui/icons-material/School";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import SubjectIcon from "@mui/icons-material/Subject";
import TopicOutlinedIcon from "@mui/icons-material/TopicOutlined";

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: 12,
  boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
  background: "linear-gradient(145deg, #ffffff, #f9f9f9)",
}));

const StyledButton = styled(Button)(({ theme }) => ({
  borderRadius: 20,
  padding: "8px 24px",
  textTransform: "none",
  fontWeight: 600,
  transition: "all 0.3s ease",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
  },
}));

function TopicPage() {
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const rowsPerPageOptions = [5, 10, 25, 50];
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [topicIdToDelete, setTopicIdToDelete] = useState(null);
  const [canAdd, setCanAdd] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [canDelete, setCanDelete] = useState(false);
  const [topicData, setTopicData] = useState([]);
  const [classData, setClassData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const navigate = useNavigate();

  // Info dialog state
  const [infoOpen, setInfoOpen] = useState(false);

  const decodeFormId = (encodedFormId) => {
    if (!encodedFormId || typeof encodedFormId !== "string") {
      console.error("encodedFormId is missing or invalid:", encodedFormId);
      return null;
    }
    try {
      const paddedEncodedFormId = encodedFormId.padEnd(
        Math.ceil(encodedFormId.length / 4) * 4,
        "="
      );
      const bytes = base64.toByteArray(paddedEncodedFormId);
      return new TextDecoder().decode(bytes);
    } catch (error) {
      console.error("Error decoding formId:", error);
      return null;
    }
  };

  const { encodedFormId } = useParams();
  const formId = decodeFormId(encodedFormId);

  const Authorizer = async () => {
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token = sessionStorage.getItem("token");
      const response = await fetch(`${apiUrl}/CPanel/Module_Authorizer`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify({ formId }),
      });

      const result = await response.json();
      const data = result[0];

      setCanEdit(data?.uModify === 1);
      setCanAdd(data?.uWrite === 1);
      setCanDelete(data?.uDelete === 1);
    } catch (error) {
      console.error("Authorization check failed:", error);
    }
  };

  const fetchClasses = async () => {
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token = sessionStorage.getItem("token");
      const response = await fetch(`${apiUrl}/Enquiry/ddlClassName`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify({}),
      });
      if (!response.ok) {
        throw new Error(`Error fetching classes: ${response.status}`);
      }
      const data = await response.json();
      if (data && Array.isArray(data)) {
        setClassData(data);
      }
    } catch (error) {
      console.error("Error fetching classes:", error);
      setClassData([]);
    }
  };

  const FetchTopic = useCallback(async () => {
    try {
      setLoading(true);
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token = sessionStorage.getItem("token");
      const response = await fetch(`${apiUrl}/Topic/FetchTopicName`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify({ TopicId: 0 }),
      });

      if (!response.ok) {
        throw new Error(`Error fetching topic data: ${response.status}`);
      }

      const data = await response.json();
      console.log("API Response:", data); // Log response for debugging

      // Check if data is an array
      const topics = Array.isArray(data)
        ? data
        : Array.isArray(data.data)
        ? data.data
        : [];
      // Map className from classData
      const enrichedTopics = topics.map((topic) => ({
        ...topic,
        className:
          classData.find((cls) => cls.classId === topic.classId)?.className ||
          "-",
      }));
      setTopicData(enrichedTopics);
    } catch (error) {
      console.error("API request error:", error);
      setTopicData([]);
    } finally {
      setLoading(false);
    }
  }, [classData]);

  useEffect(() => {
    fetchClasses();
  }, []);

  useEffect(() => {
    Authorizer();
    FetchTopic();
  }, [FetchTopic]);

  const handleClose = () => {
    setShowModal(false);
  };

  const handleEditLesson = (topicId) => {
    navigate(`/edittopicmaster/${topicId}`);
  };

  const handleAdd = () => {
    navigate("/addmastertopic");
  };

  const handleDeleteLesson = async () => {
    if (topicIdToDelete) {
      try {
        setLoading(true);
        const apiUrl = process.env.REACT_APP_BASE_URL;
        const token = sessionStorage.getItem("token");
        const response = await fetch(
          `${apiUrl}/OfflineTopicMaster/Id?TopicId=${topicIdToDelete}`,
          {
            method: "DELETE",
            headers: {
              "Content-Type": "application/json",
              Authorization: token,
            },
          }
        );
        if (!response.ok) {
          throw new Error(`Error deleting topic: ${response.status}`);
        }
        setTopicData((prevTopics) =>
          prevTopics.filter((topic) => topic.topicId !== topicIdToDelete)
        );
      } catch (error) {
        console.error("Error deleting topic:", error);
      } finally {
        setLoading(false);
        setShowModal(false);
        setTopicIdToDelete(null);
        setSnackbarMessage("Topic deleted successfully!");
        setOpenSnackbar(true);
      }
    }
  };

  const handleSearch = (event) => {
    setSearchQuery(event.target.value);
  };

  const filteredTopics = topicData.filter((topic) => {
    const className = topic?.className || "";
    const topicName = topic?.topicName || "";
    const subjectName = topic?.subjectName || "";
    const lessonName = topic?.lessionName || "";

    return (
      className.toLowerCase().includes(searchQuery.toLowerCase()) ||
      topicName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      subjectName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      lessonName.toLowerCase().includes(searchQuery.toLowerCase())
    );
  });

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
  };

  const paginatedData = filteredTopics.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  return (
    <Container maxWidth="lg">
      {/* Header Box with Title + Info Button */}
      <Box
        sx={{
          backgroundColor: "#0B1F40",
          padding: "12px 24px",
          boxShadow: 3,
          borderRadius: "4px 4px 0 0",
          margin: "2px auto",
          width: "100%",
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        <Typography variant="h4" sx={{ color: "#fff", fontWeight: "bold" }}>
          Topic Details
        </Typography>

        {/* ── Info Button ── */}
        <Tooltip title="About this page" arrow>
          <IconButton
            onClick={() => setInfoOpen(true)}
            size="small"
            sx={{
              color: "rgba(255,255,255,0.75)",
              border: "1.5px solid rgba(255,255,255,0.35)",
              borderRadius: "50%",
              width: 30,
              height: 30,
              ml: 0.5,
              transition: "all 0.2s ease",
              "&:hover": {
                color: "#ffffff",
                backgroundColor: "rgba(100,181,246,0.2)",
                borderColor: "#64B5F6",
                transform: "scale(1.1)",
                boxShadow: "0 0 8px rgba(100,181,246,0.4)",
              },
            }}
          >
            <InfoOutlinedIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      </Box>

      <Paper
        elevation={3}
        style={{ padding: 16, width: "100%", margin: "auto", marginTop: 6 }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            mb: 3,
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <TextField
            label="Search Topics"
            variant="outlined"
            value={searchQuery}
            onChange={handleSearch}
            style={{ width: "300px" }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton>
                    <SearchIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Tooltip title={!canAdd && "You are not Authorized to Add"} arrow>
            <span>
              <Button
                variant="contained"
                color="primary"
                onClick={handleAdd}
                style={{
                  width: "120px",
                  height: "50px",
                  padding: "10px 0",
                  fontWeight: "bold",
                }}
                disabled={!canAdd}
              >
                Add Topic
              </Button>
            </span>
          </Tooltip>
        </Box>

        {loading && (
          <Fade in={loading}>
            <CircularProgress sx={{ display: "block", mx: "auto", mb: 2 }} />
          </Fade>
        )}

        <Fade in={!loading}>
          <TableContainer component={Paper}>
            <Table
              sx={{
                minWidth: 650,
                border: "1px solid #ccc",
                "& .MuiTableCell-root": {
                  borderRight: "1px solid #ccc",
                  fontSize: "1rem",
                },
                "& .MuiTableHead-root .MuiTableCell-root": {
                  fontWeight: "bold",
                  backgroundColor: "#f5f5f5",
                },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      borderRight: "1px solid #ccc",
                      borderBottom: "1px solid #ccc",
                      fontWeight: "bold",
                      fontSize: "1.1rem",
                    }}
                  >
                    Class Name
                  </TableCell>
                  <TableCell
                    sx={{
                      borderRight: "1px solid #ccc",
                      borderBottom: "1px solid #ccc",
                      fontWeight: "bold",
                      fontSize: "1.1rem",
                    }}
                  >
                    Topic Name
                  </TableCell>
                  <TableCell
                    sx={{
                      borderRight: "1px solid #ccc",
                      borderBottom: "1px solid #ccc",
                      fontWeight: "bold",
                      fontSize: "1.1rem",
                    }}
                  >
                    Subject Name
                  </TableCell>
                  <TableCell
                    sx={{
                      borderRight: "1px solid #ccc",
                      borderBottom: "1px solid #ccc",
                      fontWeight: "bold",
                      fontSize: "1.1rem",
                    }}
                  >
                    Lesson Name
                  </TableCell>
                  <TableCell
                    sx={{
                      borderBottom: "1px solid #ccc",
                      borderRight: "1px solid #ccc",
                      textAlign: "center",
                      fontWeight: "bold",
                      fontSize: "1.1rem",
                    }}
                  >
                    Action
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredTopics.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <Typography variant="body1" color="text.secondary">
                        No records found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedData.map((topic) => (
                    <TableRow
                      key={topic.topicId}
                      sx={{
                        borderTop: "1px solid #ccc",
                        "&:hover": {
                          backgroundColor: "#f5f5f5",
                          transition: "background-color 0.2s",
                        },
                      }}
                    >
                      <TableCell
                        sx={{
                          borderRight: "1px solid #ccc",
                          fontSize: "1.1rem",
                        }}
                      >
                        {topic.className || "-"}
                      </TableCell>
                      <TableCell
                        sx={{
                          borderRight: "1px solid #ccc",
                          fontSize: "1.1rem",
                        }}
                      >
                        {topic.topicName || "-"}
                      </TableCell>
                      <TableCell
                        sx={{
                          borderRight: "1px solid #ccc",
                          fontSize: "1.1rem",
                        }}
                      >
                        {topic.subjectName || "-"}
                      </TableCell>
                      <TableCell
                        sx={{
                          borderRight: "1px solid #ccc",
                          fontSize: "1.1rem",
                        }}
                      >
                        {topic.lessionName || "-"}
                      </TableCell>
                      <TableCell
                        sx={{
                          textAlign: "center",
                          borderRight: "1px solid #ccc",
                          fontSize: "1.1rem",
                        }}
                      >
                        <Tooltip
                          title={!canEdit && "You are not Authorized to Edit"}
                          arrow
                        >
                          <span>
                            <Button
                              variant="contained"
                              color="warning"
                              onClick={() => handleEditLesson(topic.topicId)}
                              sx={{ marginRight: 1 }}
                              startIcon={<EditIcon />}
                              disabled={!canEdit}
                            >
                              Edit
                            </Button>
                          </span>
                        </Tooltip>
                        <Tooltip
                          title={
                            !canDelete && "You are not Authorized to Delete"
                          }
                          arrow
                        >
                          <span>
                            <Button
                              variant="contained"
                              color="error"
                              onClick={() => {
                                setTopicIdToDelete(topic.topicId);
                                setShowModal(true);
                              }}
                              disabled={!canDelete}
                              startIcon={<DeleteIcon />}
                            >
                              Delete
                            </Button>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Fade>

        {filteredTopics.length > 0 && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mt: 2,
            }}
          >
            <TextField
              select
              label="Rows per page"
              value={rowsPerPage}
              onChange={handleChangeRowsPerPage}
              sx={{ width: 150 }}
              SelectProps={{
                native: true,
              }}
            >
              {rowsPerPageOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </TextField>

            <Box>
              <Typography
                variant="body2"
                sx={{ display: "inline-block", marginRight: 2 }}
              >
                Page {page} of {Math.ceil(filteredTopics.length / rowsPerPage)}
              </Typography>
              <Button
                variant="outlined"
                onClick={() => handleChangePage(null, page - 1)}
                disabled={page === 1}
                sx={{ mr: 1 }}
              >
                Prev
              </Button>
              <Button
                variant="outlined"
                onClick={() => handleChangePage(null, page + 1)}
                disabled={
                  page >= Math.ceil(filteredTopics.length / rowsPerPage)
                }
              >
                Next
              </Button>
            </Box>
          </Box>
        )}
      </Paper>

      {/* Delete Confirmation Modal */}
      <Modal
        open={showModal}
        onClose={handleClose}
        aria-labelledby="delete-modal-title"
        aria-describedby="delete-modal-description"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 400,
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 24,
            overflow: "hidden",
          }}
        >
          <Box sx={{ backgroundColor: "#0B1F40", padding: "12px 16px" }}>
            <Typography
              id="delete-modal-title"
              variant="h6"
              sx={{ color: "#fff" }}
            >
              Delete Confirmation ⚠️
            </Typography>
          </Box>

          <Box sx={{ padding: "20px" }}>
            <Typography id="delete-modal-description" sx={{ mb: 2 }}>
              Are you sure you want to delete this topic?
            </Typography>

            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
              <Button onClick={handleClose} variant="outlined" sx={{ mr: 1 }}>
                Cancel
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={handleDeleteLesson}
              >
                Yes, Delete
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={4000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setOpenSnackbar(false)}
          severity="success"
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* ── Info Dialog ── */}
      <Dialog
        open={infoOpen}
        onClose={() => setInfoOpen(false)}
        maxWidth="sm"
        fullWidth
        sx={{
          "& .MuiDialog-paper": {
            borderRadius: "16px",
            boxShadow: "0 12px 40px rgba(11,31,61,0.2)",
            overflow: "hidden",
          },
        }}
      >
        {/* Header */}
        <DialogTitle
          sx={{
            background: "linear-gradient(135deg, #0B1F3D 0%, #1565C0 100%)",
            color: "#ffffff",
            fontWeight: 700,
            fontSize: "1.2rem",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 3,
            py: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <InfoOutlinedIcon sx={{ fontSize: 22 }} />
            Topic Details — Page Guide
          </Box>
        
        </DialogTitle>

        <DialogContent sx={{ px: 3, py: 2.5 }}>

          {/* Purpose */}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.7 }}>
            This page displays all topic records in the system. Each topic is associated with
            a class, subject, and lesson. You can search, add new topics, and edit or delete
            existing ones based on your permissions.
          </Typography>

          <Divider sx={{ mb: 2 }} />

          {/* Features */}
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.2, color: "#0B1F3D", letterSpacing: "0.4px", textTransform: "uppercase", fontSize: "0.75rem" }}>
            Features &amp; Controls
          </Typography>

          <List dense disablePadding>

            <ListItem alignItems="flex-start" sx={{ px: 0, py: 0.6 }}>
              <ListItemIcon sx={{ minWidth: 36, mt: 0.3 }}>
                <SearchIcon sx={{ fontSize: 20, color: "#1976d2" }} />
              </ListItemIcon>
              <ListItemText
                primary={<Typography variant="body2" fontWeight={600}>Search Topics</Typography>}
                secondary="Filter the topic list in real time by Class Name, Topic Name, Subject Name, or Lesson Name."
              />
            </ListItem>

            <ListItem alignItems="flex-start" sx={{ px: 0, py: 0.6 }}>
              <ListItemIcon sx={{ minWidth: 36, mt: 0.3 }}>
                <AddCircleOutlineIcon sx={{ fontSize: 20, color: "#1976d2" }} />
              </ListItemIcon>
              <ListItemText
                primary={<Typography variant="body2" fontWeight={600}>Add Topic</Typography>}
                secondary="Click 'Add Topic' to navigate to the topic creation form. Requires Add permission."
              />
            </ListItem>

            <ListItem alignItems="flex-start" sx={{ px: 0, py: 0.6 }}>
              <ListItemIcon sx={{ minWidth: 36, mt: 0.3 }}>
                <EditOutlinedIcon sx={{ fontSize: 20, color: "#1976d2" }} />
              </ListItemIcon>
              <ListItemText
                primary={<Typography variant="body2" fontWeight={600}>Edit a Topic</Typography>}
                secondary="Click the 'Edit' button on any row to modify that topic's details. Requires Edit permission."
              />
            </ListItem>

            <ListItem alignItems="flex-start" sx={{ px: 0, py: 0.6 }}>
              <ListItemIcon sx={{ minWidth: 36, mt: 0.3 }}>
                <DeleteOutlineIcon sx={{ fontSize: 20, color: "#1976d2" }} />
              </ListItemIcon>
              <ListItemText
                primary={<Typography variant="body2" fontWeight={600}>Delete a Topic</Typography>}
                secondary="Click 'Delete' on any row to open a confirmation dialog before permanently removing the topic. Requires Delete permission."
              />
            </ListItem>

          </List>

          <Divider sx={{ my: 2 }} />

          {/* Column reference */}
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.2, color: "#0B1F3D", letterSpacing: "0.4px", textTransform: "uppercase", fontSize: "0.75rem" }}>
            Table Column Reference
          </Typography>

          <List dense disablePadding>
            <ListItem alignItems="flex-start" sx={{ px: 0, py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 36, mt: 0.3 }}>
                <SchoolIcon sx={{ fontSize: 20, color: "#555" }} />
              </ListItemIcon>
              <ListItemText
                primary={<Typography variant="body2" fontWeight={600}>Class Name</Typography>}
                secondary="The class this topic is associated with."
              />
            </ListItem>
            <ListItem alignItems="flex-start" sx={{ px: 0, py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 36, mt: 0.3 }}>
                <TopicOutlinedIcon sx={{ fontSize: 20, color: "#555" }} />
              </ListItemIcon>
              <ListItemText
                primary={<Typography variant="body2" fontWeight={600}>Topic Name</Typography>}
                secondary="The name of the topic."
              />
            </ListItem>
            <ListItem alignItems="flex-start" sx={{ px: 0, py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 36, mt: 0.3 }}>
                <SubjectIcon sx={{ fontSize: 20, color: "#555" }} />
              </ListItemIcon>
              <ListItemText
                primary={<Typography variant="body2" fontWeight={600}>Subject Name</Typography>}
                secondary="The subject this topic falls under."
              />
            </ListItem>
            <ListItem alignItems="flex-start" sx={{ px: 0, py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 36, mt: 0.3 }}>
                <MenuBookIcon sx={{ fontSize: 20, color: "#555" }} />
              </ListItemIcon>
              <ListItemText
                primary={<Typography variant="body2" fontWeight={600}>Lesson Name</Typography>}
                secondary="The lesson within which this topic is covered."
              />
            </ListItem>
          </List>

          <Divider sx={{ my: 2 }} />

          {/* Permissions */}
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: "#0B1F3D", letterSpacing: "0.4px", textTransform: "uppercase", fontSize: "0.75rem" }}>
            Your Permissions
          </Typography>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Chip
              label={`Add: ${canAdd ? "✔ Allowed" : "✘ Restricted"}`}
              size="small"
              sx={{ backgroundColor: canAdd ? "#e8f5e9" : "#fce4ec", color: canAdd ? "green" : "red", fontWeight: 600 }}
            />
            <Chip
              label={`Edit: ${canEdit ? "✔ Allowed" : "✘ Restricted"}`}
              size="small"
              sx={{ backgroundColor: canEdit ? "#e8f5e9" : "#fce4ec", color: canEdit ? "green" : "red", fontWeight: 600 }}
            />
            <Chip
              label={`Delete: ${canDelete ? "✔ Allowed" : "✘ Restricted"}`}
              size="small"
              sx={{ backgroundColor: canDelete ? "#e8f5e9" : "#fce4ec", color: canDelete ? "green" : "red", fontWeight: 600 }}
            />
          </Box>

        </DialogContent>

        <DialogActions sx={{ backgroundColor: "#f5f5f5", px: 3, py: 2 }}>
          <Button
            onClick={() => setInfoOpen(false)}
            variant="contained"
            sx={{
              backgroundColor: "#0B1F3D",
              color: "#fff",
              borderRadius: "8px",
              fontWeight: 600,
              "&:hover": { backgroundColor: "#1565C0" },
            }}
          >
            Got it
          </Button>
        </DialogActions>
      </Dialog>
      {/* ── End Info Dialog ── */}

    </Container>
  );
}

export default TopicPage;